import { DateOnly } from '@if/core/date'
import { CreditTestCase } from '@src/components/LoanForm/ListCreditTestCaseDialog'
import { LoanTestCase } from '@src/components/LoanForm/ListLoanTestcaseDialog'
import {
  AddressSchemaType,
  FinanceFeeConfigurationSchemaType,
  InterestConfigurationSchemaType,
  LoanSchemaType,
  DisbursementSchemaType,
} from '@src/components/LoanForm/loan-form-schema'
import { reportErrorToServer } from '@src/services/error-logger'
import axios, { AxiosError } from 'axios'

interface ApplicantTestCaseDto {
  firstName: string
  lastName: string
  middleName: string
  birthDate: string
  languageId: number
  genderId: number
  currentAddress: AddressSchemaType
}

interface CreditTestCaseDto {
  id: string
  applicant: ApplicantTestCaseDto
  description: string
}

interface FinanceFeeConfigurationDto extends Omit<FinanceFeeConfigurationSchemaType, 'amortizationStartsOn'> {
  amortizationStartsOn: string
}

interface InterestConfigurationDto extends Omit<InterestConfigurationSchemaType, 'startDate' | 'endDate'> {
  startDate: string
  endDate: string
}

interface DisbursementDto extends Omit<DisbursementSchemaType, 'paymentOn'> {
  paymentOn: string
}

interface LoanDto
  extends Omit<
    LoanSchemaType,
    'activationPlannedOn' | 'firstPaymentExpectedOn' | 'interestConfigs' | 'financeFeeConfig' | 'disbursements'
  > {
  activationPlannedOn: string
  firstPaymentExpectedOn: string
  financeFeeConfig: FinanceFeeConfigurationDto
  interestConfigs: InterestConfigurationDto[]
  disbursements: DisbursementDto[]
}

interface LoanTestCaseDto {
  id: string
  loanData: LoanDto
  description: string
}

const instance = axios.create({
  baseURL: import.meta.env.VITE_API_TESTCASE_URL,
})

instance.interceptors.response.use(
  (respons) => {
    return respons
  },
  (error: AxiosError) => {
    reportErrorToServer(error)
    return error
  },
)

export function getCreditTestCases(): Promise<CreditTestCase[]> {
  return instance.get<CreditTestCaseDto[]>('CreditTestCases').then((response) => {
    return response.data.map<CreditTestCase>((dto, index) => ({
      id: index.toString(),
      description: dto.description,
      applicant: {
        ...dto.applicant,
        birthDate: new DateOnly(dto.applicant.birthDate),
      },
    }))
  })
}

export function getLoanTestCases(): Promise<LoanTestCase[]> {
  return instance.get<LoanTestCaseDto[]>('LoanTestCases').then((response) => {
    return response.data.map<LoanTestCase>((dto, index) => ({
      id: index.toString(),
      description: dto.description,
      loanData: {
        ...dto.loanData,
        activationPlannedOn: new DateOnly(dto.loanData.activationPlannedOn),
        firstPaymentExpectedOn: new DateOnly(dto.loanData.firstPaymentExpectedOn),
        interestConfigs: dto.loanData.interestConfigs.map((ic) => ({
          ...ic,
          startDate: new DateOnly(ic.startDate),
          endDate: new DateOnly(ic.endDate),
        })),
        financeFeeConfig: {
          ...dto.loanData.financeFeeConfig,
          amortizationStartsOn: new DateOnly(dto.loanData.financeFeeConfig.amortizationStartsOn),
        },
        disbursements: dto.loanData.disbursements.map((p) => ({
          ...p,
          paymentOn: new DateOnly(p.paymentOn),
        })),
      },
    }))
  })
}
